import { type FC, useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, Select, Switch, Tag } from 'antd';
import type { MenuProps } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import SearchBox from 'components/molecules/SearchBox';
import FilterIcon from 'assets/svg/Filter';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { tpoGenUrls } from 'config/urls';
import {
    TPO_METRIC_ATTRIBUTE_SELECT,
    TPO_METRIC_DIMENSION_SELECT,
    TPO_METRIC_FILTER,
    TPO_METRIC_FILTER_RIBBON,
    TPO_METRIC_KPI_SELECT,
    TPO_METRIC_PLAN_ACTIONS_BTN,
    TPO_METRIC_SEARCH_INPUT,
    TPO_METRIC_SUB_MODE_SWITCH,
    TPO_METRIC_TOOLBAR,
    TPO_METRIC_VIEW_SELECT,
    TPO_METRIC_YEAR_SELECT,
    tpoOptionTestId,
} from '../../config/testIds';
import { useCustomerConfig, useTpoGenConfig } from '../../context/CustomerConfigContext';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { useEditSession } from '../EditSession/EditSessionContext';
import { applyEditLockToMenu, EDIT_PLAN_MENU_KEY } from '../EditSession/utils/applyEditLockToMenu';
import EditPlanMenuLabel from '../EditSession/components/EditPlanMenuLabel';
import { buildPlanActionMenu } from '../utils/planActionMenu';
import type { PlanActionMenuSections } from '../../config/planActionMenuConfig';
import { resolvePlanMetricKpis, resolvePromoMetricKpis } from './utils/resolveSubModeDefaults';
import { resolveFieldLabel } from '../../utils/resolveFieldLabel';
import type { AttributeOption, ReportView, SubMode, TimePeriod } from './types';

const renderTag = (props: any) => <Tag>{props.label}</Tag>;

const SELECT_ALL_STYLE: React.CSSProperties = {
    padding: '5px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    fontSize: 12,
    color: '#1677ff',
};

/** Attaches an option-level id to every entry of an antd `options` array. */
const withOptionTestIds = <T extends { value: string }>(baseId: string, options: T[]) =>
    options.map((opt) => ({ ...opt, 'data-testid': tpoOptionTestId(baseId, opt.value) }));

export interface MetricToolbarProps {
    planId: string;
    reportView: ReportView;
    setReportView: (v: ReportView) => void;
    subMode: SubMode;
    setSubMode: (v: SubMode) => void;
    timePeriod: TimePeriod;
    setTimePeriod: (v: TimePeriod) => void;
    searchKeyword: string;
    setSearchKeyword: (v: string) => void;
    // Plan Metrics fields
    planDimension: string;
    setPlanDimension: (v: string) => void;
    planDimensionKpi: string;
    setPlanDimensionKpi: (v: string) => void;
    planMetricKpis: string[];
    setPlanMetricKpis: (v: string[]) => void;
    attributes: AttributeOption[];
    setAttributes: (v: AttributeOption[]) => void;
    attributeOptions: AttributeOption[];
    attributeSearch: string;
    setAttributeSearch: (v: string) => void;
    attributesLoading: boolean;
    onAttributeDropdownScroll?: () => void;
    // Promo Metrics fields
    promoDimensions: string[];
    setPromoDimensions: (v: string[]) => void;
    promoKpis: string[];
    setPromoKpis: (v: string[]) => void;
    onShowDeletePlan: () => void;
    onShowDeleteAllPromotions: () => void;
    onShowExportModal: () => void;
    /** True while an export of the active report kind for this plan is running. */
    isExportPending?: boolean;
    showTrendChart: boolean;
    onToggleTrendChart: () => void;
}

const MetricToolbar: FC<MetricToolbarProps> = ({
    planId,
    reportView,
    setReportView,
    subMode,
    setSubMode,
    timePeriod,
    setTimePeriod,
    searchKeyword,
    setSearchKeyword,
    planDimension,
    setPlanDimension,
    planDimensionKpi,
    setPlanDimensionKpi,
    planMetricKpis,
    setPlanMetricKpis,
    attributes,
    setAttributes,
    attributeOptions,
    attributeSearch,
    setAttributeSearch,
    attributesLoading,
    onAttributeDropdownScroll,
    promoDimensions,
    setPromoDimensions,
    promoKpis,
    setPromoKpis,
    onShowDeletePlan,
    onShowDeleteAllPromotions,
    onShowExportModal,
    isExportPending = false,
    showTrendChart,
    onToggleTrendChart,
}) => {
    // Metric page owns its own filter selection (decoupled from Calendar). Aliased
    // to `filterState` so the filter wiring below reads uniformly.
    const { metricFilterState: filterState, optimizedPlanId, showOptimizedView, currentPlanDetails } =
        usePlanDetailsContext();
    const { canEdit, isLockEnabled, isHeldByOther, holder, openEditPrompt } = useEditSession();
    const isOperationalPlan = Boolean(currentPlanDetails?.is_operational_plan);
    const activePlanId = showOptimizedView && optimizedPlanId ? optimizedPlanId : planId;
    const { kpiCatalog, promoKpiCatalog } = useCustomerConfig();
    const tpoGenConfig = useTpoGenConfig();
    const { metricPageConfig, metricFilterKeys, fieldValueLabelMaps } = tpoGenConfig;
    const basePlanActionMenuItems = tpoGenConfig.planActionMenuItems;
    const planActionMenuViews = tpoGenConfig.planActionMenuViews;
    const [search, setSearch] = useState<string>(searchKeyword);
    const KPI_OPTIONS = useMemo(
        () =>
            withOptionTestIds(
                TPO_METRIC_KPI_SELECT,
                kpiCatalog.map((k) => ({ label: k.name, value: k.key })),
            ),
        [kpiCatalog],
    );
    const PROMO_KPI_OPTIONS = useMemo(
        () =>
            withOptionTestIds(
                TPO_METRIC_KPI_SELECT,
                promoKpiCatalog.map((k) => ({ label: k.name, value: k.key })),
            ),
        [promoKpiCatalog],
    );
    const filterKeyMap = useMemo(() => new Map(metricFilterKeys.map((fk) => [fk.value, fk])), [metricFilterKeys]);
    const filterDataParser = useCallback(
        (rawItems: string[], resolveLabelFrom?: string) =>
            rawItems.map((name) => ({
                label: resolveFieldLabel(name, fieldValueLabelMaps, resolveLabelFrom),
                value: name,
            })),
        [fieldValueLabelMaps],
    );
    const renderFilterAttribute = useCallback(
        (key: string, attr: string | number) => (
            <span className="py-1.5">
                {resolveFieldLabel(String(attr), fieldValueLabelMaps, filterKeyMap.get(key)?.resolveLabelFrom)}
            </span>
        ),
        [fieldValueLabelMaps, filterKeyMap],
    );
    const allKpiValues = useMemo(() => KPI_OPTIONS.map((o) => o.value), [KPI_OPTIONS]);
    const allPromoKpiValues = useMemo(() => PROMO_KPI_OPTIONS.map((o) => o.value), [PROMO_KPI_OPTIONS]);
    const PLAN_DIM_OPTIONS = useMemo(
        () =>
            withOptionTestIds(
                TPO_METRIC_DIMENSION_SELECT,
                metricPageConfig.planMetricDimensions.map((d) => ({ label: d.label, value: d.value })),
            ),
        [metricPageConfig],
    );
    const PROMO_DIM_OPTIONS = useMemo(
        () =>
            withOptionTestIds(
                TPO_METRIC_DIMENSION_SELECT,
                metricPageConfig.promoMetricDimensions.map((d) => ({ label: d.label, value: d.value })),
            ),
        [metricPageConfig],
    );
    const allPromoDimValues = useMemo(() => PROMO_DIM_OPTIONS.map((o) => o.value), [PROMO_DIM_OPTIONS]);
    const REPORT_VIEW_SELECT_OPTIONS = useMemo(
        () =>
            withOptionTestIds(
                TPO_METRIC_VIEW_SELECT,
                metricPageConfig.reportViewOptions.map((r) => ({ label: r.label, value: r.value })),
            ),
        [metricPageConfig],
    );
    const TIME_PERIOD_SELECT_OPTIONS = useMemo(
        () =>
            withOptionTestIds(
                TPO_METRIC_YEAR_SELECT,
                metricPageConfig.timePeriodOptions.map((t) => ({ label: t.label, value: t.value })),
            ),
        [metricPageConfig],
    );

    const isPlan = reportView === 'plan_metrics';
    const isPlanMetric = isPlan && subMode === 'metric';
    const isPlanDimension = isPlan && subMode === 'dimension';

    const handlePlanActionClick: MenuProps['onClick'] = useCallback(
        ({ key }: { key: string }) => {
            if (key === EDIT_PLAN_MENU_KEY) openEditPrompt();
            else if (key === 'delete_plan') onShowDeletePlan();
            else if (key === 'delete_all_promotions') onShowDeleteAllPromotions();
            else if (key === 'view_trend_chart') onToggleTrendChart();
            else if (key === 'export') onShowExportModal();
        },
        [openEditPrompt, onShowDeletePlan, onShowDeleteAllPromotions, onToggleTrendChart, onShowExportModal],
    );

    const planActionMenuItems = useMemo<PlanActionMenuSections>(
        () =>
            basePlanActionMenuItems.map((section) =>
                section.map((item) => {
                    if (!item || !('key' in item)) return item;

                    const withTestId = {
                        ...item,
                        'data-testid': tpoOptionTestId(TPO_METRIC_PLAN_ACTIONS_BTN, String(item.key)),
                    };

                    if (withTestId.key === 'view_trend_chart') {
                        return {
                            ...withTestId,
                            label: showTrendChart ? 'Close Trend Chart' : 'View Trend Chart',
                            disabled: !isPlanDimension,
                        };
                    }
                    // Report export is implemented on this surface, so it escapes
                    // the blanket disable that parks the plan actions not yet
                    // built for Metrics. It is still gated on the config value
                    // and on the client-side duplicate guard.
                    if (withTestId.key === 'export') {
                        const isConfigDisabled = 'disabled' in withTestId && !!withTestId.disabled;
                        return { ...withTestId, disabled: isConfigDisabled || isExportPending };
                    }
                    return { ...withTestId, disabled: true };
                }),
            ),
        [showTrendChart, isPlanDimension, basePlanActionMenuItems, isExportPending],
    );

    // Gates the mutating keys on the lock, exactly as the Calendar toolbar does.
    // The "Edit plan" entry point it injects only survives the visibility filter
    // below when the config lists `edit_plan` on the metric surface.
    const editSessionLabels = tpoGenConfig.labels.editSession;
    const lockedMenuItems = useMemo(
        () =>
            applyEditLockToMenu(planActionMenuItems, {
                canEdit,
                isLockEnabled,
                isHeldByOther,
                isOperationalPlan,
                editLabel: (
                    <EditPlanMenuLabel
                        label={editSessionLabels.editPlan}
                        holder={isHeldByOther ? holder : null}
                        heldByOtherPrefix={editSessionLabels.heldByOtherPrefix}
                        disabledTooltip={
                            isOperationalPlan ? tpoGenConfig.labels.planDetail.operationalPlanTooltip : null
                        }
                    />
                ),
                baseTestId: TPO_METRIC_PLAN_ACTIONS_BTN,
            }),
        [
            planActionMenuItems,
            canEdit,
            isLockEnabled,
            isHeldByOther,
            isOperationalPlan,
            holder,
            editSessionLabels,
            tpoGenConfig.labels.planDetail.operationalPlanTooltip,
        ],
    );

    // Visibility runs last: only here does the injected `edit_plan` row exist to
    // be filtered out. This is also what flattens the sections for AntD.
    const visibleMenuItems = useMemo(
        () => buildPlanActionMenu(lockedMenuItems, 'metric', planActionMenuViews),
        [lockedMenuItems, planActionMenuViews],
    );

    const handleSubModeChange = useCallback(
        (checked: boolean) => setSubMode(checked ? 'metric' : 'dimension'),
        [setSubMode],
    );

    const handlePlanDimensionChange = useCallback(
        (value: string) => {
            setPlanDimension(value);
            // Attribute domain changes when dimension changes -- clear attribute selections.
            setAttributes([]);
            setAttributeSearch('');
        },
        [setPlanDimension, setAttributes, setAttributeSearch],
    );

    // Multi-select clear handlers: empty selection resets to that field's config default.
    const handlePromoDimensionsChange = useCallback(
        (v: string[]) => {
            if (v.length === 0) setPromoDimensions([...metricPageConfig.defaultPromoMetricDimensions]);
            else setPromoDimensions(v);
        },
        [setPromoDimensions, metricPageConfig],
    );

    const handlePromoKpisChange = useCallback(
        (v: string[]) => {
            if (v.length === 0) {
                const fallback = promoKpiCatalog[0]?.key ?? '';
                setPromoKpis(
                    resolvePromoMetricKpis(metricPageConfig.defaultPromoMetricKpis, promoKpiCatalog, fallback),
                );
            } else {
                setPromoKpis(v);
            }
        },
        [setPromoKpis, metricPageConfig, promoKpiCatalog],
    );

    const handlePlanMetricKpisChange = useCallback(
        (v: string[]) => {
            if (v.length === 0) {
                const fallback = kpiCatalog[0]?.key ?? '';
                setPlanMetricKpis(resolvePlanMetricKpis(metricPageConfig.defaultPlanMetricKpis, subMode, kpiCatalog, fallback));
            } else {
                setPlanMetricKpis(v);
            }
        },
        [setPlanMetricKpis, metricPageConfig, kpiCatalog, subMode],
    );

    const attributeSelectOptions = useMemo(
        () =>
            withOptionTestIds(
                TPO_METRIC_ATTRIBUTE_SELECT,
                attributeOptions.map((opt) => ({
                    label: opt.attributeDesc,
                    value: opt.attributeExId,
                })),
            ),
        [attributeOptions],
    );

    const selectedAttributeValue = attributes[0]?.attributeExId;

    const handleAttributesChange = useCallback(
        (value?: string) => {
            if (!value) {
                setAttributes([]);
                return;
            }
            const next =
                attributeOptions.find((o) => o.attributeExId === value) ??
                attributes.find((a) => a.attributeExId === value);
            setAttributes(next ? [next] : []);
        },
        [attributeOptions, attributes, setAttributes],
    );

    return (
        <>
            <div
                className="flex flex-row justify-between items-end gap-x-4 flex-wrap my-4"
                data-testid={TPO_METRIC_TOOLBAR}
            >
                <div className="flex flex-row items-end gap-x-4">
                    {/* Dimension select */}
                    <div className="flex flex-col gap-y-1">
                        <p className="text-xs text-[#52525C]">{'Dimension'}</p>
                        {isPlan ? (
                            <Select
                                data-testid={TPO_METRIC_DIMENSION_SELECT}
                                style={{ width: '10rem' }}
                                value={planDimension}
                                onChange={handlePlanDimensionChange}
                                options={PLAN_DIM_OPTIONS}
                            />
                        ) : (
                            <Select
                                mode="multiple"
                                allowClear
                                data-testid={TPO_METRIC_DIMENSION_SELECT}
                                style={{ width: '14rem' }}
                                value={promoDimensions}
                                onChange={handlePromoDimensionsChange}
                                options={PROMO_DIM_OPTIONS}
                                tagRender={renderTag}
                                placeholder="Multiple Selection"
                                maxTagCount="responsive"
                                dropdownRender={(menu) => (
                                    <>
                                        <div
                                            data-testid={`${TPO_METRIC_DIMENSION_SELECT}_select_all`}
                                            data-state-all-selected={
                                                promoDimensions.length === allPromoDimValues.length ? 'true' : undefined
                                            }
                                            style={SELECT_ALL_STYLE}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                if (promoDimensions.length === allPromoDimValues.length)
                                                    setPromoDimensions([]);
                                                else handlePromoDimensionsChange([...allPromoDimValues]);
                                            }}
                                        >
                                            {promoDimensions.length === allPromoDimValues.length
                                                ? 'Deselect All'
                                                : 'Select All'}
                                        </div>
                                        {menu}
                                    </>
                                )}
                            />
                        )}
                    </div>

                    {/* Attribute select (Plan + Metric only) — required to load metric data.
                        The helper text is absolutely positioned so it doesn't add height to
                        this column; the parent row uses items-end, and an extra line here
                        would otherwise push this column's label+Select out of alignment with
                        its siblings (Dimension, KPI) whenever no attribute is selected. */}
                    {isPlanMetric && (
                        <div className="relative flex flex-col gap-y-1">
                            <p className="text-xs text-[#52525C]">
                                {'Attribute'}
                                {!selectedAttributeValue ? (
                                    <span className="text-redTriangle ml-0.5">*</span>
                                ) : null}
                            </p>
                            <Select
                                allowClear
                                data-testid={TPO_METRIC_ATTRIBUTE_SELECT}
                                data-state-required={!selectedAttributeValue ? 'true' : undefined}
                                style={{ width: '14rem' }}
                                value={selectedAttributeValue}
                                onChange={handleAttributesChange}
                                options={attributeSelectOptions}
                                showSearch
                                filterOption={false}
                                onSearch={setAttributeSearch}
                                searchValue={attributeSearch}
                                loading={attributesLoading}
                                status={!selectedAttributeValue ? 'error' : undefined}
                                onPopupScroll={(e) => {
                                    const target = e.currentTarget;
                                    if (
                                        target.scrollTop + target.offsetHeight >= target.scrollHeight - 32 &&
                                        onAttributeDropdownScroll
                                    ) {
                                        onAttributeDropdownScroll();
                                    }
                                }}
                                placeholder="Select an attribute to load data"
                            />
                            {!selectedAttributeValue ? (
                                <p
                                    data-testid={`${TPO_METRIC_ATTRIBUTE_SELECT}_error`}
                                    className="absolute top-full left-0 mt-0.5 whitespace-nowrap text-xs text-redTriangle"
                                >
                                    {'Required to load data'}
                                </p>
                            ) : null}
                        </div>
                    )}

                    {/* KPI select */}
                    <div className="flex flex-col gap-y-1">
                        <p className="text-xs text-[#52525C]">{'KPI'}</p>
                        {isPlan && subMode === 'dimension' ? (
                            <Select
                                data-testid={TPO_METRIC_KPI_SELECT}
                                style={{ width: '12rem' }}
                                value={planDimensionKpi}
                                onChange={(v: string) => setPlanDimensionKpi(v)}
                                options={KPI_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                            />
                        ) : isPlan ? (
                            <Select
                                mode="multiple"
                                allowClear
                                data-testid={TPO_METRIC_KPI_SELECT}
                                style={{ width: '14rem' }}
                                value={planMetricKpis}
                                onChange={handlePlanMetricKpisChange}
                                options={KPI_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                                tagRender={renderTag}
                                placeholder="Multiple Selection"
                                maxTagCount="responsive"
                                dropdownRender={(menu) => (
                                    <>
                                        <div
                                            data-testid={`${TPO_METRIC_KPI_SELECT}_select_all`}
                                            data-state-all-selected={
                                                planMetricKpis.length === allKpiValues.length ? 'true' : undefined
                                            }
                                            style={SELECT_ALL_STYLE}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                if (planMetricKpis.length === allKpiValues.length)
                                                    setPlanMetricKpis([]);
                                                else handlePlanMetricKpisChange([...allKpiValues]);
                                            }}
                                        >
                                            {planMetricKpis.length === allKpiValues.length
                                                ? 'Deselect All'
                                                : 'Select All'}
                                        </div>
                                        {menu}
                                    </>
                                )}
                            />
                        ) : (
                            <Select
                                mode="multiple"
                                allowClear
                                data-testid={TPO_METRIC_KPI_SELECT}
                                style={{ width: '14rem' }}
                                value={promoKpis}
                                onChange={handlePromoKpisChange}
                                options={PROMO_KPI_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                                tagRender={renderTag}
                                placeholder="Multiple Selection"
                                maxTagCount="responsive"
                                dropdownRender={(menu) => (
                                    <>
                                        <div
                                            data-testid={`${TPO_METRIC_KPI_SELECT}_select_all`}
                                            data-state-all-selected={
                                                promoKpis.length === allPromoKpiValues.length ? 'true' : undefined
                                            }
                                            style={SELECT_ALL_STYLE}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                if (promoKpis.length === allPromoKpiValues.length) setPromoKpis([]);
                                                else handlePromoKpisChange([...allPromoKpiValues]);
                                            }}
                                        >
                                            {promoKpis.length === allPromoKpiValues.length
                                                ? 'Deselect All'
                                                : 'Select All'}
                                        </div>
                                        {menu}
                                    </>
                                )}
                            />
                        )}
                    </div>
                </div>

                <div className="flex flex-row justify-end items-end gap-x-3 flex-wrap">
                    <SearchBox
                        search={search}
                        setSearch={setSearch}
                        onSearch={() => setSearchKeyword(search)}
                        width={180}
                        data-testid={TPO_METRIC_SEARCH_INPUT}
                    />

                    {isPlan && (
                        <div className="flex flex-row items-center gap-x-2 pb-1">
                            <span
                                className={`text-xs ${subMode === 'dimension' ? 'text-[#18181B]' : 'text-[#A1A1AA]'}`}
                            >
                                {'Dimension'}
                            </span>
                            <Switch
                                data-testid={TPO_METRIC_SUB_MODE_SWITCH}
                                data-state-active={subMode === 'metric' ? 'true' : undefined}
                                checked={subMode === 'metric'}
                                onChange={handleSubModeChange}
                            />
                            <span className={`text-xs ${subMode === 'metric' ? 'text-[#18181B]' : 'text-[#A1A1AA]'}`}>
                                {'Metric'}
                            </span>
                        </div>
                    )}

                    <div className="flex flex-col gap-y-1">
                        <p className="text-xs text-[#52525C]">{'Year'}</p>
                        <Select
                            data-testid={TPO_METRIC_YEAR_SELECT}
                            style={{ width: '8rem' }}
                            value={timePeriod}
                            onChange={(v: TimePeriod) => setTimePeriod(v)}
                            options={TIME_PERIOD_SELECT_OPTIONS}
                        />
                    </div>

                    <div className="flex flex-col gap-y-1">
                        <p className="text-xs text-[#52525C]">{'View'}</p>
                        <Select
                            data-testid={TPO_METRIC_VIEW_SELECT}
                            style={{ width: '10rem' }}
                            value={reportView}
                            onChange={(v: ReportView) => setReportView(v)}
                            options={REPORT_VIEW_SELECT_OPTIONS}
                        />
                    </div>

                    <div className="self-end pb-0">
                        <NewGlobalFilter
                            disabled={isPlan}
                            availableFilters={filterState.availableFilters}
                            allCheckedAttribute={filterState.allCheckedAttribute}
                            allSelectedValueForAllAttribute={filterState.allSelectedValueForAllAttribute}
                            applyFilterObject={filterState.applyFilterObject}
                            resetFilterObject={filterState.resetFilterObject}
                            onSubmitHandler={filterState.onFilterSaveClickHandler}
                            url={tpoGenUrls.getReportDataFilterAttributes(activePlanId)}
                            selectedFilter={filterState.selectedFilter}
                            setSelectedFilter={filterState.setSelectedFilter}
                            tempApplyFilterObject={filterState.tempApplyFilterObject}
                            setTempApplyFilterObject={filterState.setTempApplyFilterObject}
                            apiVariables={filterState.filterApiVariables}
                            filterDimensionInfo={tpoGenConfig.metricFilterDimensionInfo}
                            showAttributePanelToLeft
                            filterIcon={<FilterIcon />}
                            dataParser={filterDataParser}
                            data-testid={TPO_METRIC_FILTER}
                        />
                    </div>

                    <Dropdown
                        menu={{ items: visibleMenuItems, onClick: handlePlanActionClick }}
                        trigger={['click']}
                        overlayStyle={{ width: '12rem', borderRadius: '0.5rem' }}
                    >
                        <Button data-testid={TPO_METRIC_PLAN_ACTIONS_BTN}>
                            <div className="flex flex-row justify-start items-center">
                                <EllipsisOutlined />
                            </div>
                        </Button>
                    </Dropdown>
                </div>
            </div>
            {!isPlan && (
                <div className="mb-3 mt-2">
                    <FilterRibbon
                        allCheckedAttribute={filterState.allCheckedAttribute}
                        allSelectedValueForAllAttribute={filterState.allSelectedValueForAllAttribute}
                        applyFilterObject={filterState.applyFilterObject}
                        resetFilterObject={filterState.resetFilterObject}
                        onSubmitHandler={filterState.onFilterSaveClickHandler}
                        filterKeys={metricFilterKeys}
                        renderAttribute={renderFilterAttribute}
                        data-testid={TPO_METRIC_FILTER_RIBBON}
                    />
                </div>
            )}
        </>
    );
};

MetricToolbar.displayName = 'MetricToolbar';
export default MetricToolbar;
